import * as React from 'react';
import { css } from '@emotion/css';
import {
  Divider,
  Empty,
  Input,
  List,
  notification,
  Tooltip,
  Typography,
} from 'antd';
import { groupBy, mapValues } from 'lodash';
import { SearchOutlined } from '@ant-design/icons';
import { getRecentSearches } from '../../../api/apis';
import Loader from '../../shared/Loader';

export interface ISearchHistoryProps {}
const searchHistoryContainer = css`
  .search-input-wrapper {
    background: #2696ff;
    padding: 1rem;
    text-align: center;
    .ant-input-affix-wrapper {
      max-width: 400px;
      display: flex !important;
      margin: auto;

      svg {
        fill: #1c72e8;
        transform: scale(1.35);
      }
      input {
        margin-left: 6px;
      }
    }
  }

  .search-items-lists {
    overflow: auto;
    overflow-x: hidden;
    max-height: calc(100vh - 160px);
    .ant-empty {
      margin: 5rem 8px;
    }
    .ant-list-bordered {
      border-top: none;
      border-bottom: none;
      padding-top: 1rem;

      &:last-child {
        border-bottom: 1px solid #d9d9d9;
      }
      .ant-list-header {
        border-bottom: none;
      }
    }
  }
`;

type searchHistoryItemType = Array<Record<string | number | symbol, any>>;

export default function SearchHistory(_props: ISearchHistoryProps) {
  const [query, setQuery] = React.useState<
    string | number | readonly string[] | undefined
  >('');
  const [data, setData] = React.useState<searchHistoryItemType>([]);
  const [loading, setLoading] = React.useState<boolean>(true);

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setQuery(value);
  };

  const renderLists = () => {
    const groupedLists = mapValues(groupBy(data, 'timestamp'));

    const groupedListsHeaders = Object.keys(groupedLists);

    if (groupedListsHeaders.length === 0) {
      return (
        <Empty
          description={<span>We couldn't find any recent searches!</span>}
        />
      );
    }

    return groupedListsHeaders.map((headerKey, index) => {
      let dataSource: searchHistoryItemType = [];
      if (query) {
        groupedLists[headerKey].forEach(listItem => {
          if (
            listItem.key.toLowerCase().includes(query) ||
            listItem.timestamp.toLowerCase().includes(query)
          ) {
            dataSource.push(listItem);
          }
        });
      } else {
        dataSource = groupedLists[headerKey];
      }

      if (dataSource.length === 0) return null;

      return (
        <List
          key={headerKey}
          header={
            <Divider style={{ margin: 0 }} plain>
              <b>{headerKey}</b>
            </Divider>
          }
          bordered
          dataSource={dataSource}
          renderItem={item => (
            <List.Item>
              <>
                {item.key}{' '}
                <Tooltip title={`searched ${item.count} times`}>
                  <Typography.Text>{item.count}</Typography.Text>
                </Tooltip>
              </>
            </List.Item>
          )}
          footer={
            index === groupedListsHeaders.length - 1 ? (
              <Divider plain>That's all folks! 🤐</Divider>
            ) : null
          }
        />
      );
    });
  };
  const fetchSearchHistory = async () => {
    setLoading(true);

    try {
      const recentSearches = await getRecentSearches();
      setData(
        (recentSearches.length ? recentSearches : []).map((item: any) => ({
          ...item,
          timestamp: new Date(item.timestamp).toLocaleDateString('en-US', {
            weekday: 'long',
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          }),
        }))
      );
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Error fetching the search history!',
      });
    } finally {
      setLoading(false);
    }
  };
  React.useEffect(() => {
    fetchSearchHistory();
  }, []);

  return (
    <div className={searchHistoryContainer}>
      <Loader loading={loading}>
        <div className="search-input-wrapper">
          <Input
            placeholder="Search for recent queries"
            value={query}
            onChange={handleQueryChange}
            prefix={<SearchOutlined />}
          />
        </div>
        <div className="search-items-lists">{renderLists()}</div>
      </Loader>
    </div>
  );
}
